import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useCallback, useDebugValue } from 'react';
import { Device } from './devices.types';
import {
  createCompanyDevice,
  deviceProviderSelectors,
  fetchCompanyDevices as fetchCompanyDevicesAsyncThunk,
  removeCompanyDevice as removeCompanyDeviceAsyncThunk,
} from './devices.state';
import { AppDispatch } from '../../index';
import { AddDevice, RemoveCompanyDevice } from './devices.api';

export type UseDevicesByCompanyId = (params: { companyId: string }) => [
  Device[],
  {
    removeCompanyDevice: (params: Pick<Parameters<RemoveCompanyDevice>[0], 'deviceId'>) => void;
    addNewDevice: (params: { deviceId: string }) => void;
    fetchCompanyDevices: () => void;
  }
];

export const useDevicesByCompanyId: UseDevicesByCompanyId = ({ companyId }) => {
  const dispatch: AppDispatch = useDispatch();
  const state = useSelector<RootState, RootState['devices']>(state => state.devices);
  const companyDevices = useSelector<RootState, RootState['devices']['companyDevices']>(
    state => state.devices.companyDevices
  );

  const companySelector = companyDevices[companyId] || [];

  const devices =
    companySelector.length === 0
      ? []
      : companySelector.reduce((acc, s) => {
          const item = deviceProviderSelectors.selectById(state, s);
          if (item) {
            acc.push(item);
          }
          return acc;
        }, [] as Device[]);

  const fetchCompanyDevices = useCallback(() => {
    dispatch(fetchCompanyDevicesAsyncThunk({ companyId }));
  }, [dispatch, companyId]);

  const removeCompanyDevice = useCallback(
    (params: Pick<Parameters<RemoveCompanyDevice>[0], 'deviceId'>) => {
      dispatch(removeCompanyDeviceAsyncThunk({ ...params, companyId }));
    },
    [dispatch, companyId]
  );

  const addNewDevice = useCallback(
    (params: { deviceId: string }) => {
      dispatch(createCompanyDevice({ companyId, deviceId: params.deviceId }));
    },
    [dispatch, companyId]
  );

  useDebugValue(devices);

  return [devices, { fetchCompanyDevices, removeCompanyDevice, addNewDevice }];
};
